'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useRouter } from 'next/navigation'

import { useTranslation } from '@/hooks/client/use-translation'
import type { CompareFeaturedProduct } from '@/lib/supabase/get-compare-featured'

interface SwapButtonProps {
  otherProductSlug: string
  side: 'a' | 'b'
  featuredProducts: CompareFeaturedProduct[]
}

interface SearchResult {
  id: number
  name: string
  slug: string
  productSlug: string | null
  imageUrl: string | null
  price: number | null
  priceType: string | null
}

const ProductRow = ({
  product,
  onSelect,
  formatPrice,
}: {
  product: {
    id: number
    name: string
    slug: string
    imageUrl: string | null
    price: number | null
    priceType: string | null
  }
  onSelect: (slug: string) => void
  formatPrice: (price: number | null, priceType: string | null) => string
}) => (
  <button
    type="button"
    data-tracking="ignore"
    onClick={() => onSelect(product.slug)}
    className="flex h-[89px] cursor-pointer gap-x-4 rounded-[10px] p-3 text-left hover:bg-[#142546]/5"
  >
    {product.imageUrl ? (
      <img
        src={product.imageUrl}
        alt={product.name}
        className="border-navy/20 h-[65px] w-[65px] rounded-[13.33px] border object-contain p-1"
        loading="lazy"
      />
    ) : (
      <div className="border-navy/20 h-[65px] w-[65px] rounded-[13.33px] border bg-slate-50" />
    )}
    <div className="grid min-w-0">
      <span className="text-navy truncate text-[16px] leading-[22.4px] font-medium tracking-[-0.48px]">
        {product.name}
      </span>
      <span className="text-[14px] leading-[19.6px] font-medium tracking-[-0.42px] text-[#14254699]">
        {formatPrice(product.price, product.priceType)}
      </span>
    </div>
  </button>
)

export const SwapButton = ({ otherProductSlug, side, featuredProducts }: SwapButtonProps) => {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (open) {
      dialogRef.current?.showModal()
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      dialogRef.current?.close()
      setSearchTerm('')
      setSearchResults([])
    }
  }, [open])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    if (searchTimer.current) {
      clearTimeout(searchTimer.current)
    }
    if (term.length < 3) {
      setSearchResults([])
      return
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true)
      try {
        const resp = await fetch(`/api/search-nav/releases?q=${encodeURIComponent(term)}&s=6`)
        if (resp.ok) {
          const data = await resp.json()
          const results: SearchResult[] = (data.results ?? []).map(
            (r: {
              id: number
              name: string
              slug: string
              productSlug?: string
              imageUrl?: string
              openGraphImages?: string[]
              price?: number
              priceType?: string
            }) => {
              // Last OG image is the model product image
              const ogImages = r.openGraphImages ?? []
              const modelImage = ogImages.length > 0 ? ogImages[ogImages.length - 1] : null
              return {
                id: r.id,
                name: r.name,
                slug: r.slug,
                productSlug: r.productSlug ?? null,
                imageUrl: modelImage ?? r.imageUrl ?? null,
                price: r.price ?? null,
                priceType: r.priceType ?? null,
              }
            }
          )
          setSearchResults(results)
        }
      } finally {
        setSearching(false)
      }
    }, 300)
  }, [])

  const handleSelectProduct = useCallback(
    (releaseSlug: string) => {
      // Navigate to the compare URL for the new pair. Build it from
      // window.location.pathname (the public address-bar path) rather than
      // usePathname(): on this proxy-rewritten route usePathname() returns the
      // internal `/{locale}/{segment}/hearing-aids/compare/...` form, and
      // pushing that would leak the segment/locale rewrite into the URL bar.
      // window.location keeps the public locale prefix (or none) and no segment.
      const pathParts = window.location.pathname.split('/')
      const compareIdx = pathParts.indexOf('compare')
      if (compareIdx === -1) return

      const slugA = side === 'a' ? releaseSlug : otherProductSlug
      const slugB = side === 'b' ? releaseSlug : otherProductSlug
      pathParts[compareIdx + 1] = `${slugA}-vs-${slugB}`

      router.push(pathParts.join('/'))
      setOpen(false)
    },
    [otherProductSlug, side, router]
  )

  const { t } = useTranslation()

  const formatPrice = (price: number | null, priceType: string | null): string => {
    if (!price) return ''
    const type = priceType === 'single' ? t('price.perEach', '/ each') : t('price.perPair', '/ pair')
    return `${t('price.from', 'From')} $${price.toLocaleString('en-US')} ${type}`
  }

  const showResults = searchTerm.length >= 3 && searchResults.length > 0
  const products = showResults ? searchResults : []

  return (
    <>
      <button
        type="button"
        data-tracking="ignore"
        onClick={() => setOpen(true)}
        className="text-navy/60 ring-navy/15 hover:bg-navy hover:ring-navy absolute -right-1 -bottom-1 inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white shadow-sm ring-1 transition-all duration-200 hover:text-white focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-95 md:right-0 md:bottom-0"
        aria-label={t('aria.swapProduct', 'Swap product')}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
          <path d="M8 16H3v5" />
        </svg>
      </button>

      <dialog
        ref={dialogRef}
        onClose={() => setOpen(false)}
        className="backdrop:bg-overlay fixed inset-0 z-50 m-auto w-screen rounded-t-[20px] border border-[#14254666] bg-white p-0 shadow-xl md:w-[700px] md:rounded-[20px]"
      >
        <div className="grid h-auto gap-y-8 overflow-x-hidden px-10 py-9 text-left sm:gap-y-0 lg:h-[480px]">
          {/* Title */}
          <span className="text-navy max-h-10 border-b border-[#142546]/10 pb-5 text-[20px] leading-[24px] font-semibold tracking-[-0.6px] sm:border-b-0 sm:pb-0 lg:text-[32px] lg:leading-[38.4px] lg:tracking-[-0.96px]">
            {t('compare.addProduct', 'Add Product')}
          </span>

          <div className="grid gap-y-8">
            {/* Search input */}
            <div className="relative flex h-[52px] w-full border-b border-[#142546]/60">
              <div className="absolute top-0 mr-3 mb-3 grid h-10 w-10 items-center justify-center rounded-[60px] bg-[#142546]/5">
                <svg
                  className="h-5 w-5 text-slate-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="text-navy ml-[52px] h-10 w-full bg-transparent text-[20px] leading-7 font-medium tracking-[-0.6px] focus:outline-hidden lg:text-[24px] lg:leading-[33.6px] lg:tracking-[-0.72px]"
                placeholder={t('compare.searchPlaceholder', 'Search hearing aids')}
              />
              {searching ? (
                <div className="absolute top-3 right-0 h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500" />
              ) : null}

              {/* Search results dropdown */}
              {showResults ? (
                <div className="absolute top-[52px] left-0 z-10 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
                  <div className="no-scrollbar max-h-[300px] overflow-y-auto p-2">
                    {products.map((product) => (
                      <ProductRow
                        key={product.id}
                        product={{ ...product, slug: product.productSlug ?? product.slug }}
                        onSelect={handleSelectProduct}
                        formatPrice={formatPrice}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Featured Products — always visible */}
            <div className="grid gap-y-4">
              <span className="text-[16px] leading-[20.8px] font-semibold tracking-[-0.48px] text-[#142546]/90">
                {t('compare.featuredProducts', 'Featured Products')}
              </span>
              <div className="no-scrollbar grid h-[395px] w-full grid-cols-1 gap-y-2 overflow-y-scroll lg:h-[192px] lg:grid-cols-2">
                {featuredProducts.map((product) => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    onSelect={handleSelectProduct}
                    formatPrice={formatPrice}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Close button */}
        <button
          type="button"
          data-tracking="ignore"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-slate-400 transition-colors hover:text-slate-600"
          aria-label={t('aria.close', 'Close')}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </dialog>
    </>
  )
}
